import React, { useEffect, useRef, useState } from "react";
import "./offerform.scss";
import BreadCrumsHeader from "../BreadCrums";
import OfferFormLayout from "./OfferFormLayout";
import CategorySelection from "./CategorySelection";
import SubCategorySelection from "./SubCategorySelection";
import OfferCreation from "./OfferCreation";
import CustomizeOffer from "./CustomizeOffer";
import RulesRegulations from "./RulesRegulations";
import Location from "./Location";
import OfferReview from "./OfferReview";
import MerchantSelection from "./MerchantSelection";
import GenericPopup from "../GenericPopup";
import PrimaryOutlineButton from "../PrimaryOutlineButton";
import PrimaryButton from "../PrimaryButton";
import InfoIcon from "../../../Assets/Icons/info.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { store } from "../../../redux/store";
import {
  createOffer,
  getCategories,
  getOfferById,
  deleteOffer,
} from "../../../services/offerService";
import { resetOfferState } from "../../../redux/slices/offerSlice";
import { logout } from "../../../services/authService";
import { getFcmToken } from "../../../utils/connectStore";
import toast from "../../../utils/toast";

const OfferForm = ({ merchant }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id = null } = location?.state || "";

  // Use local state instead of useSelector to avoid reducer execution conflicts
  const [role, setRole] = useState(null);
  const [offerData, setOfferData] = useState(null);
  const [reduxSelectedMerchant, setReduxSelectedMerchant] = useState(null);

  // Determine initial step based on role
  const [stepIndex, setStepIndex] = useState(1);
  const [subCategories, setSubCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(undefined);
  const [payload, setPayload] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editOfferData, setEditOfferData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null); // For admin merchant selection
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingNavigatePath, setPendingNavigatePath] = useState(null);
  const [pendingAction, setPendingAction] = useState("navigate");
  const topRef = useRef(null);

  // Safely update state from Redux store to avoid reducer execution conflicts
  useEffect(() => {
    const updateState = () => {
      try {
        const state = store.getState();
        const newRole = state?.authReducer?.tokenData?.user?.role;
        const newOfferData = state?.offerReducer?.createdOffer;
        const newSelectedMerchant = state?.merchantReducer?.selectedMerchent;

        setRole(newRole);
        setOfferData(newOfferData);
        setReduxSelectedMerchant(newSelectedMerchant);
      } catch (error) {
        console.warn('OfferForm store access error:', error.message);
      }
    };

    // Initial state update with delay to avoid reducer execution conflicts
    const timeoutId = setTimeout(updateState, 0);
    return () => clearTimeout(timeoutId);
  }, []);

  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      try {
        const state = store.getState();
        const newRole = state?.authReducer?.tokenData?.user?.role;
        const newOfferData = state?.offerReducer?.createdOffer;
        const newSelectedMerchant = state?.merchantReducer?.selectedMerchent;

        setRole(prev => {
          if (prev !== newRole) return newRole;
          return prev;
        });
        setOfferData(prev => {
          if (prev !== newOfferData) return newOfferData;
          return prev;
        });
        setReduxSelectedMerchant(prev => {
          if (prev !== newSelectedMerchant) return newSelectedMerchant;
          return prev;
        });
      } catch (error) {
        console.warn('OfferForm store subscription error:', error.message);
      }
    });

    return unsubscribe;
  }, []);

  // Update stepIndex when role changes
  useEffect(() => {
    if (role === "admin") {
      setStepIndex(1);
    }
  }, [role]);

  // Helper function to get step numbers based on role
  const getStepNumber = (baseStep) => {
    return role === "admin" ? baseStep + 1 : baseStep;
  };

  const getOfferData = async () => {
    const getPromise = await dispatch(getOfferById({ id: id }))?.unwrap();
    console.log(getPromise, "getPromise1234567890")
    if (getPromise?.success === true) {
      setEditOfferData(getPromise?.data);
    }
  };

  const getCategoriesData = async () => {
    const categoryPromise = await dispatch(getCategories())?.unwrap();
    if (categoryPromise?.success === true) {
      setCategories(categoryPromise?.data);
    }
  };

  useEffect(() => {
    getCategoriesData();
  }, []);

  useEffect(() => {
    if (id) {
      setIsEdit(true);
      getOfferData();
    } else {
      setIsEdit(false);
      setEditOfferData(null);
      // For a brand new offer, we must clear any leftover draft data from Redux
      dispatch(resetOfferState());
    }
  }, [id, dispatch]);

  // Handle resumption logic when editing an existing offer
  useEffect(() => {
    if (isEdit && editOfferData && role) {
      const status = (editOfferData.status || "").toLowerCase();

      // Helper to find the first incomplete base step (1-7)
      const findResumptionStep = (data) => {
        // 1. Category Selection
        if (!data.category) return 1;

        // 2. Sub Category Selection
        const subs = data.subCategory || [];
        if (subs.length === 0) return 2;

        // 3. Offer Creation (About Offer)
        // Check for core identity: Title + Description/Images
        const hasTitle = data.title && String(data.title).trim() !== "";
        const hasMainInfo = data.thumbnailImage || (data.previewImages && data.previewImages.length > 0) || (data.description && String(data.description).trim() !== "");

        if (!hasTitle || !hasMainInfo) return 3;

        // 4. Customisation
        // Check for specific fields collected in Step 4: redemptionMethod, redemptionFrequency, start/end dates
        // If it looks like Step 4 hasn't been submitted/saved properly, return 4
        if (!data.redemptionMethod && !data.redemptionFrequency && !data.startDate) return 4;

        // 5. Rules and Regulations
        // Check for custom rules or the logic collected in Step 5
        const rules = data.ruleAndRegulation || [];
        if (rules.length === 0) return 5;

        // 6. Location (Only for INSTORE offers)
        if (data.offerType === "INSTORE_SWOODLE_OFFER") {
          const locs = data.tempLocations || data.locations || [];
          if (!locs || locs.length === 0) return 6;
        }

        // 7. Review and Approve
        return (data.offerType === "INSTORE_SWOODLE_OFFER") ? 7 : 6;
      };

      // Case A: Non-Draft (Pending, Approved, Declined, etc.)
      // Always start from the very beginning for review
      if (status && status !== "drafted") {
        const resetStep = (role === "admin") ? 2 : 1;
        if (stepIndex !== resetStep) {
          console.log(`[Resumption] Status is '${status}'. Resetting to review-start Step ${resetStep}.`);
          setStepIndex(resetStep);
        }
        return;
      }

      // Case B: Drafted offers
      // Go to the exact step where the user actually left off
      // Only trigger this if we are still at the initial/first screen
      if (stepIndex === 1 || (role === "admin" && stepIndex === 2)) {
        const baseStep = findResumptionStep(editOfferData);
        const resumedStep = getStepNumber(baseStep);

        if (resumedStep !== stepIndex) {
          console.log(`[Resumption] Draft detected. Resuming at Base Step ${baseStep} (Layout Step ${resumedStep})`);
          setStepIndex(resumedStep);
        }
      }
    }
  }, [isEdit, editOfferData, role]);

  const handleStepClick = (target) => {
    // Prevent navigating to Merchant selection when editing as admin
    if (role === "admin" && isEdit && target === 1) return;
    setStepIndex(target);
  };

  const offersPath = role === "admin" ? "/offer-list" : "/merchant/offer-list";

  const items = [
    {
      title: "Offers",
      href: offersPath,
    },
    {
      title: isEdit ? "Edit an Offer" : "Create an Offer",
    },
  ];

  const handlePrevious = () => {
    let previousStep = stepIndex - 1;
    // Prevent admin (editing) from going back to merchant selection
    if (role === "admin" && isEdit && previousStep === 1) {
      previousStep = 2;
    }
    if (previousStep < 1) {
      previousStep = 1;
    }
    setStepIndex(previousStep);
  };

  const setPayloadData = (data) => {
    setPayload(data);
  };

  const handleSaveAndExit = async () => {
    // If we have a payload, attempt to save current progress as a draft
    if (payload !== null) {
      try {
        const currentOfferId = id || offerData?._id || editOfferData?._id;

        // Backend validation workaround: 
        // If locations are empty and not 'all stores', the backend throws an error.
        // We strip these fields to prevent the error toast when exiting.
        const safePayload = { ...payload };
        if (
          Array.isArray(safePayload.locations) &&
          safePayload.locations.length === 0 &&
          !safePayload.availableAcrossAllStores
        ) {
          delete safePayload.locations;
          delete safePayload.availableAcrossAllStores;
        }

        // Only dispatch if we still have meaningful data to update
        // (Checking keys > 0 might be good, but even empty payload with ID is harmless usually, 
        // though strictly 'safePayload' might just be empty if it only had locations)
        if (currentOfferId && Object.keys(safePayload).length > 0) {
          await dispatch(createOffer({
            ...safePayload,
            offerId: currentOfferId,
            isCreated: false,
            ...(role === "admin" && selectedMerchant && { merchantId: selectedMerchant._id })
          })).unwrap();
          toast.success("Progress saved successfully");
        }
      } catch (error) {
        console.error("Save progress error:", error);
      }
    }
    navigate(role === 'admin' ? "/offer-list" : "/merchant/offer-list");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    // On mount, if we are creating a new offer, ensure state is clean
    if (!id) {
      dispatch(resetOfferState());
      setPayload(null);
      setCategoryId(undefined);
    }
    return () => {
      dispatch(resetOfferState());
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [stepIndex]);

  // Reset offer state when merchant changes to prevent permission issues
  useEffect(() => {
    if (selectedMerchant && offerData?.merchant?._id !== selectedMerchant._id) {
      dispatch(resetOfferState());
    }
  }, [selectedMerchant, offerData?.merchant?._id, dispatch]);

  // Guard (admin any, merchant create): intercept back button and mark session for sidebar
  useEffect(() => {
    const shouldGuard = role === "admin" || (role === "merchant" && !isEdit);
    if (!shouldGuard) return;

    // Mark a session flag for sidebar/navigation interception
    sessionStorage.setItem("ADMIN_OFFER_CREATION_GUARD", "true");

    // Remove native beforeunload prompt; use custom modal instead
    const handleBeforeUnload = null;

    const handlePopState = () => {
      // push back to current page and show modal
      window.history.pushState(null, "", window.location.pathname);
      setShowLeaveConfirm(true);
      setPendingNavigatePath("/offer-list");
    };

    const handleAttemptExit = (evt) => {
      const targetPath = evt?.detail?.path || "/offer-list";
      setPendingNavigatePath(targetPath);
      if (evt?.detail?.action) {
        setPendingAction(evt.detail.action);
      } else {
        setPendingAction("navigate");
      }
      setShowLeaveConfirm(true);
    };

    // Do not attach native beforeunload to avoid browser toast
    window.history.pushState(null, "", window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("attempt-exit-offer-form", handleAttemptExit);

    // Intercept refresh shortcuts (F5, Ctrl/Cmd+R) to show custom modal
    const keydownHandler = (e) => {
      const isRefreshKey =
        e.key === "F5" ||
        e.keyCode === 116 ||
        ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "r"));
      if (isRefreshKey) {
        e.preventDefault();
        setPendingAction("reload");
        setShowLeaveConfirm(true);
      }
    };
    window.addEventListener("keydown", keydownHandler, true);

    // Intercept Link (<a>) navigations globally
    const clickCapture = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href]');
      if (!anchor) return;

      try {
        const href = anchor.getAttribute('href') || '';
        const hasDownload = anchor.hasAttribute('download');
        const bypass = anchor.getAttribute('data-bypass-exit-guard') === 'true';

        // Allow downloads and blob/object URLs without guard
        if (hasDownload || href.startsWith('blob:') || bypass) {
          return; // don't intercept
        }

        // Intercept in-app navigations only
        if (!href.startsWith('http')) {
          e.preventDefault();
          setPendingNavigatePath(href);
          setShowLeaveConfirm(true);
        }
      } catch (_) { }
    };
    document.addEventListener('click', clickCapture, true);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("attempt-exit-offer-form", handleAttemptExit);
      window.removeEventListener("keydown", keydownHandler, true);
      document.removeEventListener('click', clickCapture, true);
      sessionStorage.removeItem("ADMIN_OFFER_CREATION_GUARD");
    };
  }, [role, isEdit]);

  return (
    <div ref={topRef} className="offerFormPage">
      {/* Step 1: Select Merchant (Admin only) */}
      {role === "admin" && !isEdit && stepIndex === 1 && (
        <OfferFormLayout
          content={
            <MerchantSelection
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              selectedMerchant={selectedMerchant}
              setSelectedMerchant={setSelectedMerchant}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          handlePrevious={handlePrevious}
          showPrevious={false}
          showNext={true}
        />
      )}

      {/* Category Selection */}
      {stepIndex === getStepNumber(1) && (
        <OfferFormLayout
          content={
            <CategorySelection
              setSubCategories={setSubCategories}
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              categoryId={categoryId}
              setCategoryId={setCategoryId}
              setPayloadData={setPayloadData}
              categories={categories}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              handleSaveAndExit={handleSaveAndExit}
              payload={payload}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={!(merchant === true && role !== "admin" && stepIndex === getStepNumber(1))}
        />
      )}

      {/* Sub Category Selection */}
      {stepIndex === getStepNumber(2) && (
        <OfferFormLayout
          content={
            <SubCategorySelection
              categoryId={categoryId}
              subCategories={subCategories}
              setSubCategories={setSubCategories}
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              categories={categories}
              handleSaveAndExit={handleSaveAndExit}
              payload={payload}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={true}
        />
      )}

      {/* Offer Creation */}
      {stepIndex === getStepNumber(3) && (
        <OfferFormLayout
          content={
            <OfferCreation
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              handleSaveAndExit={handleSaveAndExit}
              payload={payload}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={true}
        />
      )}

      {/* Customize Offer */}
      {stepIndex === getStepNumber(4) && (
        <OfferFormLayout
          content={
            <CustomizeOffer
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              handleSaveAndExit={handleSaveAndExit}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={true}
        />
      )}

      {/* Rules and Regulations */}
      {stepIndex === getStepNumber(5) && (
        <OfferFormLayout
          content={
            <RulesRegulations
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              handleSaveAndExit={handleSaveAndExit}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={true}
        />
      )}

      {/* Location (for INSTORE_SWOODLE_OFFER only) */}
      {stepIndex === getStepNumber(6) && offerData?.offerType === "INSTORE_SWOODLE_OFFER" && (
        <OfferFormLayout
          content={
            <Location
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              handleSaveAndExit={handleSaveAndExit}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
          handlePrevious={handlePrevious}
          showPrevious={true}
        />
      )}

      {/* Review and Approve */}
      {((stepIndex === getStepNumber(7) && offerData?.offerType === "INSTORE_SWOODLE_OFFER") ||
        (stepIndex === getStepNumber(6) && offerData?.offerType !== "INSTORE_SWOODLE_OFFER")) && (
          <OfferFormLayout
            content={
              <OfferReview
                handlePrevious={handlePrevious}
                editOfferData={editOfferData}
                setPayloadData={setPayloadData}
                selectedMerchant={selectedMerchant}
              />
            }
            stepIndex={stepIndex}
            handleSaveAndExit={handleSaveAndExit}
            onStepClick={handleStepClick}
            handlePrevious={handlePrevious}
            showPrevious={true}
          />
        )}
      {showLeaveConfirm && (
        <GenericPopup
          icon={InfoIcon}
          content={"Are you sure you want to leave? Your progress will be saved as a draft so you can finish it later."}
          popupClassName="w-[335px] sm:w-[470px] pb-8"
          cancelBtn={
            <PrimaryOutlineButton
              label="No, Stay"
              className="px-6 font-poppins text-[13px] md:text-[14px] text-center font-[500] h-[40px] md:h-[42px] rounded-[8px] mt-6 transition-all hover:bg-[#F0FBFF]"
              onClick={() => setShowLeaveConfirm(false)}
            />
          }
          continueBtn={
            <PrimaryButton
              label="Yes, Save and Exit"
              className="px-6 font-poppins text-[13px] md:text-[14px] text-center font-[500] h-[40px] md:h-[42px] rounded-[8px] mt-6 transition-all hover:opacity-90 shadow-sm"
              onClick={async () => {
                setShowLeaveConfirm(false);
                sessionStorage.removeItem("ADMIN_OFFER_CREATION_GUARD");
                if (pendingAction === "logout") {
                  // Standard logout flow
                  const currentUserRole = role;
                  const result = await dispatch(logout({ fcmToken: getFcmToken() }))?.unwrap();
                  const target = (currentUserRole && currentUserRole.toLowerCase() === "fundraiser")
                    ? "/fundraiser/account-login"
                    : "/";
                  navigate(target);
                } else {
                  // Save progress and exit
                  await handleSaveAndExit();
                }
              }}
            />
          }
        />
      )}
    </div>
  );
};

export default OfferForm;